import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  Upload, 
  Download, 
  FileText, 
  Trash2,
  ClipboardList,
  Loader2,
  CheckCircle
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Card, CardContent } from '../../../components/ui/card';
import { toast } from 'sonner';
import { API_BASE } from '../../../config/api';

const API = `${API_BASE}/api`;

export default function RubricStep({ programmeId, programme, token, headers, onUpdate, onRefreshStatus }) {
  const fileInputRef = useRef(null);
  const [rubric, setRubric] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    pass_percentage: 50
  });

  useEffect(() => {
    if (programmeId) {
      fetchRubric();
    } else {
      setLoading(false);
    }
  }, [programmeId]);

  const fetchRubric = async () => {
    try {
      const res = await axios.get(`${API}/programmes/${programmeId}/rubric`, { headers });
      if (res.data.rubric) {
        setRubric(res.data.rubric);
        setFormData({
          title: res.data.rubric.title || '',
          description: res.data.rubric.description || '',
          pass_percentage: res.data.rubric.pass_percentage || 50
        });
      }
    } catch (error) {
      console.error('Failed to fetch rubric:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!formData.title) {
      toast.error('Please enter a rubric title first');
      return;
    }

    setUploading(true);
    const data = new FormData();
    data.append('file', file);
    data.append('title', formData.title);
    data.append('description', formData.description);
    data.append('pass_percentage', formData.pass_percentage);

    try {
      await axios.post(
        `${API}/programmes/${programmeId}/rubric`,
        data,
        { headers: { ...headers, 'Content-Type': 'multipart/form-data' } }
      );
      toast.success('Rubric uploaded successfully');
      fetchRubric();
      if (onRefreshStatus) onRefreshStatus();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload rubric');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownload = async () => {
    try {
      const res = await axios.get(`${API}/programmes/${programmeId}/rubric/download`, {
        headers,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = rubric?.filename || 'rubric.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download rubric');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this rubric?')) return;

    try {
      await axios.delete(`${API}/programmes/${programmeId}/rubric`, { headers });
      toast.success('Rubric deleted');
      setRubric(null);
      if (onRefreshStatus) onRefreshStatus();
    } catch (error) {
      toast.error('Failed to delete rubric');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (!programmeId) {
    return (
      <div className="text-center py-12">
        <ClipboardList className="h-12 w-12 text-zinc-600 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-white mb-2">Save Programme First</h3>
        <p className="text-zinc-400">Complete Step 1 to upload rubric</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Description */}
      <div className="p-4 bg-amber-500/10 rounded-lg border border-amber-500/30">
        <p className="text-sm text-amber-300">
          Upload the grading rubric document that facilitators will use to assess learner submissions. 
          Supported formats: PDF, DOCX, DOC, XLSX, XLS
        </p>
      </div>

      {rubric ? (
        /* Existing Rubric Display */
        <Card className="bg-zinc-800/50 border-zinc-700">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start gap-4">
                <div className="p-3 rounded-lg bg-amber-500/20">
                  <FileText className="h-8 w-8 text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">{rubric.title}</h3>
                  {rubric.description && (
                    <p className="text-sm text-zinc-400 mt-1">{rubric.description}</p>
                  )}
                  <div className="flex items-center gap-4 mt-3 text-sm text-zinc-500">
                    <span>{rubric.filename}</span>
                    <span>{formatFileSize(rubric.file_size)}</span>
                    <span>Pass: {rubric.pass_percentage}%</span>
                  </div>
                  <p className="text-xs text-zinc-600 mt-2">
                    Uploaded: {new Date(rubric.uploaded_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownload}
                  className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Download
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDelete}
                  className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-zinc-700">
              <div className="flex items-center gap-2 text-emerald-400">
                <CheckCircle className="h-5 w-5" />
                <span className="text-sm font-medium">Rubric uploaded successfully</span>
              </div>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* Upload Form */
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-zinc-300">Rubric Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Assessment Rubric 2024"
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-zinc-300">Pass Percentage</Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={formData.pass_percentage}
                onChange={(e) => setFormData({ ...formData, pass_percentage: parseInt(e.target.value) || 50 })}
                className="bg-zinc-800 border-zinc-700 text-white mt-1"
              />
            </div>
          </div>

          <div>
            <Label className="text-zinc-300">Description (optional)</Label>
            <Input
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description of the rubric"
              className="bg-zinc-800 border-zinc-700 text-white mt-1"
            />
          </div>

          <div 
            className="border-2 border-dashed border-zinc-700 rounded-lg p-8 text-center cursor-pointer hover:border-amber-500/50 transition-colors"
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleUpload}
              accept=".pdf,.docx,.doc,.xlsx,.xls"
              className="hidden"
            />
            {uploading ? (
              <div className="flex flex-col items-center">
                <Loader2 className="h-12 w-12 text-amber-400 animate-spin mb-4" />
                <p className="text-zinc-300">Uploading rubric...</p>
              </div>
            ) : (
              <>
                <Upload className="h-12 w-12 text-zinc-500 mx-auto mb-4" />
                <p className="text-lg font-medium text-white mb-2">
                  Drop rubric file here or click to browse
                </p>
                <p className="text-sm text-zinc-400">
                  PDF, DOCX, DOC, XLSX, XLS up to 50MB
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
